import React from "react";

import { StatusBadge } from "../common/SurfaceUI";
import Img from "../common/Img";
import lbl from "../../data/Labels.json";
import TimetableActions from "./TimetableActions";

const isHolidayRow = (row) =>
  row?.date_type === 2 ||
  row?.date_type === "2" ||
  (!row?.lecture_id && (row?.date_title || row?.the_date));

const timeLabel = (row) => {
  const start = row.start_time || "";
  const end = row.end_time || "";
  if (start && end) return `${start} - ${end}`;
  return start || end || row.time || "—";
};

const groupDayRows = (rows) => {
  const groups = [];
  const slots = new Map();
  rows.forEach((row, index) => {
    if (isHolidayRow(row)) {
      groups.push({ type: "holiday", key: `holiday-${row.the_date || index}`, rows: [row] });
      return;
    }
    const key = `${row.start_time || ""}|${row.end_time || ""}`;
    if (!slots.has(key)) {
      const group = { type: "slot", key: `slot-${key}-${index}`, rows: [] };
      slots.set(key, group);
      groups.push(group);
    }
    slots.get(key).rows.push(row);
  });
  return groups;
};

const HolidayCard = ({ row }) => (
  <div className="timetable-lecture is-holiday" role="listitem">
    <div className="timetable-holiday-area">
      {row.date_picture ? (
        <img className="timetable-holiday-cover" src={row.date_picture} alt="" onError={(e) => e.currentTarget.remove()} />
      ) : null}
      <strong className="timetable-holiday-title">{row.date_title || lbl.TimetableHoliday}</strong>
    </div>
  </div>
);

const LectureCard = ({ row, classes, onSaved, attendanceDate, stacked = false }) => {
  const teacher =
    [
      row.substitute_teacher_lastname,
      row.substitute_teacher_name,
      row.teacher_lastname,
      row.teacher_name,
    ]
      .map((value) => String(value || "").trim())
      .find(Boolean) || "—";
  const state = Number(row.attendance_state_id ?? row.attendance_state ?? 0);
  return (
    <article className={`timetable-lecture${stacked ? " is-stacked" : ""}`} role="listitem">
      <div className="timetable-time">
        <span>زمان</span>
        <strong dir="ltr">{timeLabel(row)}</strong>
      </div>
      <div className="timetable-lecture-primary">
        <div className="timetable-subject-line">
          <strong>
            {row.subject_name || "—"}
            {row.group_name ? ` - گروپ ${row.group_name}` : ""}
          </strong>
        </div>
        <span>{row.class_desc || "صنف مشخص نشده"}</span>
      </div>
      <dl className="timetable-lecture-meta">
        <div>
          <dt>{lbl.Teacher}</dt>
          <dd>
            <span className="timetable-teacher-photo" aria-hidden="true">
              <Img
                src={
                  row.susbstitute_teacher_photo ||
                  row.substitute_teacher_photo ||
                  row.teacher_photo ||
                  "/parity-assets/aria_university_logo.png"
                }
              />
            </span>
            {state ? (
              <i
                className={`timetable-attendance-state state-${state}`}
                aria-label={state === 1 ? "حاضر" : "غیر حاضر"}
              />
            ) : null}
            {teacher}
          </dd>
        </div>
        <div>
          <dt>{lbl.TimetableLocation}</dt>
          <dd>{row.location_name || "—"}</dd>
        </div>
      </dl>
      <div className="timetable-lecture-flags" aria-label="جزئیات درس">
        <span
          className={`timetable-practical-state${Number(row.is_practical) === 1 ? " is-active" : ""}`}
          title={Number(row.is_practical) === 1 ? "عملی" : "نظری"}
        >
          <svg aria-hidden="true" viewBox="0 0 8.513 8.513">
            <path
              d="M38.3,31.637a1.847,1.847,0,0,0-1.383.54,1.39,1.39,0,0,0-.29,1.637l-.352.351L37.55,35.44l.349-.352a1.391,1.391,0,0,0,1.637-.292,1.85,1.85,0,0,0,.453-1.873l-1.113,1.113-.961-.241-.24-.959,1.114-1.113A1.908,1.908,0,0,0,38.3,31.637ZM34.184,33.6a.97.97,0,0,0-.606.215l-.009,0-1.521,1.228v0a.979.979,0,0,0-.219,1.514l0,0,.041.041,2.439,2.4a.491.491,0,0,0,.848-.26.645.645,0,0,0,.333.092.655.655,0,0,0,.655-.655.551.551,0,0,0-.008-.092.655.655,0,0,0,.99-.563.655.655,0,0,0,.452-1.129h0l-2.415-2.406-.327-.394ZM32.4,38.037l-.573.572A.9.9,0,0,0,33.1,39.884l.57-.571Z"
              transform="translate(-31.564 -31.636)"
            />
          </svg>
        </span>
        {row.substitute_teacher_name || Number(row.is_substitute) === 1 ? (
          <StatusBadge tone="success">{lbl.Substitute}</StatusBadge>
        ) : null}
      </div>
      <div className="timetable-roster-action">
        <TimetableActions row={row} classes={classes} lectureDate={attendanceDate} onSaved={onSaved} />
      </div>
    </article>
  );
};

const WEEKDAYS = [
  { id: "1", name: "شنبه" },
  { id: "2", name: "یکشنبه" },
  { id: "3", name: "دوشنبه" },
  { id: "4", name: "سه‌شنبه" },
  { id: "5", name: "چهارشنبه" },
];

const dateForDay = (weekStart, offset) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(weekStart || ""))) return "";
  const date = new Date(`${weekStart}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + offset);
  return date.toISOString().slice(0, 10);
};

const TimetableList = ({ data, query, weekStart, classes, onSaved }) => {
  const rows = Array.isArray(data?.data) ? data.data : [];
  const normalizedQuery = String(query || "").trim().toLowerCase();
  const filtered = normalizedQuery
    ? rows.filter((row) =>
        Object.values(row || {}).some(
          (value) => value != null && String(value).toLowerCase().includes(normalizedQuery)
        )
      )
    : rows;

  const grouped = new Map(
    WEEKDAYS.map((day, index) => [
      day.id,
      {
        name: day.name,
        date: dateForDay(weekStart, index),
        rows: [],
      },
    ])
  );

  filtered.forEach((row) => {
    const key = String(row.weekday_id || "");
    if (!grouped.has(key)) return;
    const day = grouped.get(key);
    if (!day.date) {
      day.date =
        row.attendance_date || row.lecture_date || row.day_date || row.the_date || "";
    }
    day.rows.push(row);
  });

  return (
    <div className="timetable-schedule" aria-label="برنامه هفتگی">
      {[...grouped.entries()].map(([key, day]) => (
        <section className="timetable-day-card" key={key}>
          <header>
            <div>
              <span className="timetable-day-kicker">روز هفته</span>
              <h2>{day.name}</h2>
              {day.date ? (
                <time className="timetable-day-date" dateTime={day.date}>
                  {day.date}
                </time>
              ) : null}
            </div>
            <span className="timetable-day-count">
              {day.rows.filter((row) => !isHolidayRow(row)).length} جلسه
            </span>
          </header>
          <div className="timetable-day-rows" role="list">
            {!day.rows.length ? <div className="timetable-day-empty">درسی ثبت نشده است</div> : null}
            {groupDayRows(day.rows).map((slot) => {
              if (slot.type === "holiday") {
                return <HolidayCard key={slot.key} row={slot.rows[0]} />;
              }
              if (slot.rows.length === 1) {
                return (
                  <LectureCard
                    key={slot.rows[0].lecture_id || slot.key}
                    row={slot.rows[0]}
                    classes={classes}
                    attendanceDate={day.date}
                    onSaved={onSaved}
                  />
                );
              }
              return (
                <section className="timetable-parallel-slot" key={slot.key}>
                  <header>
                    <span dir="ltr">{timeLabel(slot.rows[0])}</span>
                    <strong>{slot.rows.length} درس همزمان</strong>
                  </header>
                  <div className="timetable-parallel-items">
                    {slot.rows.map((row, index) => (
                      <LectureCard
                        key={row.lecture_id || `${slot.key}-${index}`}
                        row={row}
                        classes={classes}
                        attendanceDate={day.date}
                        onSaved={onSaved}
                        stacked
                      />
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
};

export default TimetableList;
